import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { MessageCircle } from "lucide-react";

interface SectionBannerAdsProps {
  categorySlug: string;
}

interface BannerAd {
  id: string;
  category_slug: string;
  position: number;
  image_url: string | null;
  link_url: string | null;
  start_date: string | null;
  end_date: string | null;
  is_active: boolean;
}

export const SectionBannerAds = ({ categorySlug }: SectionBannerAdsProps) => {
  const { i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const { data: ads = [] } = useQuery({
    queryKey: ["section-banner-ads", categorySlug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("section_banner_ads" as any)
        .select("*")
        .eq("category_slug", categorySlug)
        .eq("is_active", true)
        .order("position", { ascending: true });
      if (error) throw error;
      return (data as unknown as BannerAd[]) || [];
    },
  });

  const { data: whatsappSetting } = useQuery({
    queryKey: ["banner_ads_whatsapp"],
    queryFn: async () => {
      const { data } = await supabase
        .from("site_settings")
        .select("setting_value")
        .eq("setting_key", "banner_ads_whatsapp")
        .maybeSingle();
      return (data?.setting_value as any) ?? { phone: "" };
    },
  });

  const phone = (whatsappSetting?.phone || "").replace(/[^\d]/g, "");
  const whatsappMsg = encodeURIComponent(
    isRTL
      ? `مرحباً، أرغب باستئجار مساحة إعلانية في قسم: ${categorySlug}`
      : `Hello, I'd like to rent an ad space in section: ${categorySlug}`
  );
  const whatsappUrl = phone ? `https://wa.me/${phone}?text=${whatsappMsg}` : "#";

  // Filter by date range
  const now = new Date();
  const isWithinDateRange = (ad: BannerAd) => {
    if (ad.start_date && new Date(ad.start_date) > now) return false;
    if (ad.end_date && new Date(ad.end_date) < now) return false;
    return true;
  };

  // Build 3 slots
  const slots = [1, 2, 3].map((pos) => {
    const ad = ads.find((a) => a.position === pos && isWithinDateRange(a));
    return { position: pos, ad };
  });

  return (
    <div className="container px-3 sm:px-4 mb-4 sm:mb-6">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        {slots.map(({ position, ad }) => {
          if (ad && ad.image_url) {
            const inner = (
              <img
                src={ad.image_url}
                alt={isRTL ? "إعلان" : "Advertisement"}
                className="w-full aspect-video object-cover rounded-xl shadow-md hover:shadow-lg transition-shadow"
                loading="lazy"
              />
            );
            return ad.link_url ? (
              <a key={position} href={ad.link_url} target="_blank" rel="noopener noreferrer" className="block focus-visible:ring-2 focus-visible:ring-primary rounded-xl">
                {inner}
              </a>
            ) : (
              <div key={position} className="rounded-xl overflow-hidden">{inner}</div>
            );
          }

          // Empty -> rental placeholder
          return (
            <a
              key={position}
              href={whatsappUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center justify-center aspect-video rounded-xl border-2 border-dashed border-primary/40 bg-gradient-to-br from-primary/5 to-secondary/5 hover:border-primary hover:from-primary/10 hover:to-secondary/10 transition-all p-3 text-center group focus-visible:ring-2 focus-visible:ring-primary"
              aria-label={isRTL ? "تواصل معنا لتأجير هذه المساحة الإعلانية" : "Contact us to rent this ad space"}
            >
              <MessageCircle className="w-5 h-5 text-primary mb-1 group-hover:scale-110 transition-transform" aria-hidden="true" />
              <p className="text-xs sm:text-sm font-bold text-foreground">
                {isRTL ? "مساحة إعلانية للتأجير" : "Ad Space For Rent"}
              </p>
              <p className="text-[10px] sm:text-xs text-muted-foreground">
                {isRTL ? "تواصل معنا عبر واتساب" : "Contact us on WhatsApp"}
              </p>
            </a>
          );
        })}
      </div>
    </div>
  );
};